import { useMemo } from 'react';

import { UsageMetricType } from '@superfunnel/types/usage-limits';
import {
  LocalLimitCheckResult,
  canPerformOperationLocal,
} from '@superfunnel/utils/check-limit';

import { useAuthContext } from '../contexts/AuthContext';
import { useDashboardContext } from '../contexts/DashboardContext';
import { useUserContext } from '../contexts/UserContext';
import { useAccountUsage } from './useAccountUsage';

/**
 * Hook that checks whether the current account can perform an operation
 * against a usage metric, using locally available data (no API call).
 *
 * @param metric - The usage metric to check
 * @param increment - How many units the operation would consume (default: 1)
 * @returns Result with allowed flag, current/limit/remaining counts, and message
 */
export function useCanPerformOperation(
  metric: UsageMetricType,
  increment: number = 1,
): LocalLimitCheckResult | null {
  const { selectedAccount } = useUserContext();
  const { subscription, isSubscriptionLoaded } = useDashboardContext();
  const { isSupportAdmin } = useAuthContext();
  const usage = useAccountUsage(selectedAccount?.id);

  return useMemo(() => {
    const account = selectedAccount?.data();
    if (!account || !usage || !isSubscriptionLoaded) return null;

    // Extract current usage for the metric
    let currentUsage: number;
    if (metric === 'aiTokens') {
      currentUsage = usage.aiTokens?.spendTotal ?? 0;
    } else {
      currentUsage = (usage[metric as keyof typeof usage] as number) ?? 0;
    }

    const result = canPerformOperationLocal(
      metric,
      currentUsage,
      account,
      subscription?.data(),
      increment,
    );

    if (isSupportAdmin) {
      return {
        ...result,
        allowed: true,
      };
    }

    return result;
  }, [
    selectedAccount,
    subscription,
    isSubscriptionLoaded,
    usage,
    metric,
    increment,
    isSupportAdmin,
  ]);
}
